import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import BrandTitle from "../components/BrandTitle";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";

export default function GeofencingTiirada() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "Geofencing Without PostGIS: How 'Gig Near You' Finds Workers in TiiRADA",
        "July 30, 2026",
        "6min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title"><BrandTitle text={postTitle} /></h1>

            <p className="lead">
                TiiRADA's most valuable notification isn't a bid or a message — it's{" "}
                <strong>"Gig Near You"</strong>, the push a worker gets the moment a client posts a
                job in their area. This post walks through the geofencing layer that powers it: no
                PostGIS, no spatial database, just a bounding-box constant, the geocoder gem's
                Haversine math, and a smart fallback.
            </p>

            <h2 className="post-sect">The geofence: a plain bounding box</h2>
            <p>
                TiiRADA operates in one region — Davao del Norte. Rather than pulling in a
                spatial extension, the service area is defined with a simple bounding box constant:
            </p>
            <pre>
                <code className="language-ruby">{`# app/models/job_post.rb (coordinates generalized for illustration)
SERVICE_AREA_GEOFENCE = {
  min_lat: 7.0,
  max_lat: 8.1,
  min_lng: 125.3,
  max_lng: 125.85
}.freeze`}</code>
            </pre>
            <p>
                The bounds were tuned deliberately against real geographical boundaries (including
                island municipalities and excluding bordering provinces). The box isn't a marketing
                approximation — it was hand-tuned against actual regional borders.
            </p>

            <h2 className="post-sect">Keeping pins inside the fence</h2>
            <p>
                When a client drops a pin, Rails validates it in two stages. First the cheap
                bounding-box check; then, only for points near the edge, an expensive reverse
                geocoding check that confirms the province:
            </p>
            <pre>
                <code className="language-ruby">{`# app/models/job_post.rb (simplified validation)
def validate_service_area_geofence
  return if latitude.blank? || longitude.blank?
  # Optimization: only geocode externally if something relevant changed
  return unless will_save_change_to_latitude? ||
                will_save_change_to_longitude? ||
                will_save_change_to_location?

  unless latitude.between?(min_lat, max_lat) && longitude.between?(min_lng, max_lng)
    errors.add(:location, "pin is outside our current service area.")
    return
  end

  # Secondary Province Check (for points near the border)
  result = Geocoder.search([latitude, longitude]).first
  if result && result.state != "Davao del Norte"
    errors.add(:location, "pin is in #{result.state}. TiiRADA is currently exclusive to Davao del Norte.")
  end
rescue StandardError => e
  # If geocoder fails, rely on the bounding box which is now tighter
end`}</code>
            </pre>
            <p>
                The two-stage design keeps costs sane: the bounding box is free and instant, so
                only edge candidates burn a Nominatim lookup. And if the geocoder times out (5s
                timeout configured), the validation degrades gracefully to the bounding box instead
                of blocking the client.
            </p>
            <p>
                The mobile app mirrors the same bounds client-side (in{" "}
                <code>jobPostForm.ts</code>) so a client gets an early "outside service area" alert
                before ever hitting the API.
            </p>

            <h2 className="post-sect">Haversine in Ruby: the 15 km push</h2>
            <p>
                When a job post is created, the controller fans out to nearby workers. Distance is
                computed with the <code>geocoder</code> gem's <code>distance_to</code> — pure Ruby
                Haversine, no database geometry:
            </p>
            <pre>
                <code className="language-ruby">{`# app/controllers/api/v1/job_posts_controller.rb (simplified illustration)
def notify_relevant_tiradors_of_open_job(job_post, exclude_user_ids: [])
  potential_tiradors = User.where(role: [ "tirador", "both" ])
                           .where.not(id: [ current_user.id, *exclude_user_ids ].uniq)

  recipient_ids = []

  potential_tiradors.includes(:profile).find_each do |u|
    if u.notify_all_gigs
      recipient_ids << u.id
      next
    end

    profile = u.profile
    next unless profile

    skills = profile.skills || []
    skill_match = skills.empty? || skills.include?(job_post.service_type)
    next unless skill_match

    proximity_match = if profile.latitude.present? && job_post.latitude.present?
      profile.distance_to([ job_post.latitude, job_post.longitude ]) <= 15
    else
      profile_town = profile.town.to_s.strip.downcase
      job_town = job_post.location.to_s.split(",").last.to_s.strip.downcase
      profile_town.present? && job_town.present? && profile_town == job_town
    end

    recipient_ids << u.id if proximity_match
  end

  title = "Gig Near You"
  message = "#{job_post.title} • #{job_post.location.presence || 'Nearby'}"

  User.where(id: recipient_ids.uniq).find_each do |u|
    NotificationJob.perform_later(u.id, title, message, job_post.id, "JobPost", image_url)
  end

  recipient_ids.uniq
end`}</code>
            </pre>
            <p>
                The candidate scan layers three filters in order of cost:
            </p>
            <ul className="lead">
                <li><strong>Role</strong> — only <code>tirador</code> or <code>both</code> roles (workers), never other clients.</li>
                <li><strong>Skills</strong> — the worker's profile skills must include the job's service type (or their skill list is empty, meaning "open to anything").</li>
                <li><strong>Proximity</strong> — Haversine distance ≤ 15 km.</li>
            </ul>

            <h2 className="post-sect">The fallback that matters</h2>
            <p>
                The most honest detail in the code is the <code>else</code> branch. Not every
                worker has coordinates — location is optional on a profile. So when either side is
                missing lat/lng, the code falls back to string matching: the worker's{" "}
                <code>town</code> compared against the last comma-separated token of the job's
                human-readable <code>location</code>:
            </p>
            <pre>
                <code className="language-ruby">{`profile_town = profile.town.to_s.strip.downcase
job_town = job_post.location.to_s.split(",").last.to_s.strip.downcase
profile_town.present? && job_town.present? && profile_town == job_town`}</code>
            </pre>
            <p>
                It's coarse — but it guarantees a worker without GPS still gets notified for jobs
                in their declared town, instead of silently dropping out of the pool.
            </p>

            <h2 className="post-sect">The bypass: notify_all_gigs</h2>
            <p>
                A small but important knob: users with <code>notify_all_gigs = true</code> skip
                both the skill and proximity checks entirely — they want every open job, regardless
                of distance. Everything else passes through the three filters.
            </p>

            <h2 className="post-sect">Different distances, different purposes</h2>
            <p>
                The 15 km radius is only for the push. The discovery feed reuses the same
                coordinates with a different threshold — 30 km — via the geocoder's SQL{" "}
                <code>.near</code> scope (which emits a bounding box plus a Haversine predicate in
                the query itself):
            </p>
            <pre>
                <code className="language-ruby">{`# Home feed (GET /api/v1/job_posts)
scope = JobPost.where.not(user_id: current_user.id)
               .active_discovery
               .within_davao_del_norte_geofence

if current_user.profile&.latitude.present? &&
   current_user.profile&.longitude.present?
  scope = scope.near(
    [ current_user.profile.latitude, current_user.profile.longitude ],
    30,
    order: nil
  )
end`}</code>
            </pre>
            <p>
                So the radius widens as the intent widens: a <strong>push</strong> interrupts you
                at 15 km, but <strong>scrolling</strong> the feed shows you a broader 30 km world.
                Both are anchored to the same profile coordinates.
            </p>

            <h2 className="post-sect">Why no PostGIS?</h2>
            <p>
                A spatial extension would be the "proper" answer, but TiiRADA's numbers never
                needed it. The fan-out loop runs in Ruby over a plain composite index on{" "}
                <code>profiles(latitude, longitude)</code>. For a regional marketplace, the
                bounded candidate pool is small enough that in-Ruby Haversine finishes comfortably
                inside the request. The bounding box keeps the SQL fast, and the town fallback
                covers the coordinate-less edge cases. It's a reminder that the right data
                structure is the one that fits the actual scale — not the most impressive one.
            </p>

            <h2 className="post-sect">Summary</h2>
            <p>
                "Gig Near You" is three tiers of geography working together: a hand-tuned bounding
                box that keeps every pin inside Davao del Norte, a Ruby Haversine check that pushes
                to workers within 15 km, and a town-string fallback that keeps GPS-less workers in
                the loop. No PostGIS required — just geocoder's distance math, a composite index,
                and a fallback that refuses to drop anyone silently.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}